import { EMAIL_REGEX } from '@/constants/constants';

/**
 * The Boards v2 alpha's CSV (IL-107): the emails read from a file, and the
 * review rows the Boards v2 Rollout page shows once each one has been checked
 * against RunDiffusion's accounts (the lookupBoardsV2AlphaEmails callable,
 * firebase/functions/src/useCases/boardsV2Alpha.ts).
 */

// The callables take this many emails or people a call (BOARDS_V2_ALPHA_BATCH)
export const ALPHA_BATCH: number = 200;
// A file with more is refused: split it
export const ALPHA_MAX_EMAILS: number = 2000;
export const ALPHA_MAX_FILE_BYTES: number = 5 * 1024 * 1024;

// The lookup's answer, as firebase/functions/src/types/onCallData.ts has it
export interface BoardsV2AlphaTeam {
	id: string
	name: string
	companyId: string | null
	companyName: string | null
	boardsV2: boolean
}
export interface BoardsV2AlphaMatch {
	email: string
	userId: string | null
	deleted: boolean
	isAdmin: boolean
	inAlpha: boolean
	teams: BoardsV2AlphaTeam[]
}

export interface CsvEmails {
	// trimmed, in lower case, each once, in the file's order
	emails: string[]
	// cells with an @ that aren't an email
	invalid: string[]
	// emails seen again, dropped
	repeats: number
}

/**
 * Every email in a CSV, or any text. Each cell is read whatever its column,
 * so a header row, names and other columns are passed over, and
 * "Ana Ruiz <Ana@Studio.com>" gives ana@studio.com.
 */
export function readCsvEmails (text: string): CsvEmails {
	const seen: Set<string> = new Set();
	const out: CsvEmails = { emails: [], invalid: [], repeats: 0 };
	text.split(/[\r\n,;\t]+/).forEach((cell: string) => {
		const raw: string = cell.replace(/"/g, '').trim();
		if (!raw.includes('@')) return;
		const found: string[] = raw.match(/[^\s<>()[\]]+@[^\s<>()[\]]+/g) || [raw];
		found.forEach((token: string) => {
			const email: string = token.replace(/^mailto:/i, '').replace(/\.+$/, '').toLowerCase();
			if (seen.has(email)) {
				out.repeats++;
				return;
			}
			seen.add(email);
			(EMAIL_REGEX.test(email) ? out.emails : out.invalid).push(email);
		});
	});
	return out;
}

export type AlphaRowStatus =
	| 'checking'
	| 'error' // the check failed: check again
	| 'ready' // an account, not in yet
	| 'in-alpha' // in already
	| 'admin' // a RunDiffusion admin, who has Boards v2 anyway
	| 'added' // added from this review
	| 'missing' // no account has the email
	| 'deleted' // the account was deleted
	| 'invalid'; // not an email

export interface AlphaReviewRow {
	email: string
	status: AlphaRowStatus
	userId: string | null
	teams: BoardsV2AlphaTeam[]
}

export type AlphaRowGroup = 'ready' | 'has-it' | 'not-found' | 'pending';

export function alphaRowGroup (status: AlphaRowStatus): AlphaRowGroup {
	if (status === 'ready') return 'ready';
	if (status === 'in-alpha' || status === 'admin' || status === 'added') return 'has-it';
	if (status === 'missing' || status === 'deleted' || status === 'invalid') return 'not-found';
	return 'pending';
}

export function rowFromMatch (match: BoardsV2AlphaMatch): AlphaReviewRow {
	let status: AlphaRowStatus = 'ready';
	if (!match.userId) status = 'missing';
	else if (match.deleted) status = 'deleted';
	else if (match.inAlpha) status = 'in-alpha';
	else if (match.isAdmin) status = 'admin';
	return { email: match.email, status, userId: match.userId, teams: match.teams || [] };
}

export interface AlphaMembership {
	company: string | null
	// the account's teams in that company, or one team outside a company
	teams: string
}

/** Where an account works: each company with its teams, then teams outside a company. */
export function groupMemberships (teams: Array<Pick<BoardsV2AlphaTeam, 'name' | 'companyId' | 'companyName'>>): AlphaMembership[] {
	const byCompany: Map<string, { company: string, teams: string[] }> = new Map();
	const loose: AlphaMembership[] = [];
	teams.forEach((team) => {
		const teamName: string = team.name || '(no name)';
		if (!team.companyId) {
			loose.push({ company: null, teams: teamName });
			return;
		}
		const entry = byCompany.get(team.companyId) || { company: team.companyName || '(no name)', teams: [] };
		entry.teams.push(teamName);
		byCompany.set(team.companyId, entry);
	});
	return [
		...Array.from(byCompany.values()).map((c) => ({ company: c.company, teams: c.teams.join(', ') })),
		...loose,
	];
}

/** The same in a line: "Studio Co: Design, Render · Freelancers" */
export function membershipLabel (teams: Array<Pick<BoardsV2AlphaTeam, 'name' | 'companyId' | 'companyName'>>): string {
	return groupMemberships(teams).map((m: AlphaMembership) => (m.company ? `${m.company}: ${m.teams}` : m.teams)).join(' · ');
}

/** "3 not in the system and 2 already in are left out." */
export function leftOutNote (parts: Array<{ count: number, label: string }>): string {
	const shown = parts.filter((p) => p.count > 0);
	if (!shown.length) return '';
	const total: number = shown.reduce((n: number, p) => n + p.count, 0);
	const phrases: string[] = shown.map((p) => `${p.count} ${p.label}`);
	const list: string = phrases.length > 1 ? `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}` : phrases[0];
	return `${list} ${total === 1 ? 'is' : 'are'} left out.`;
}
